import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { mfiApi } from "../../api";
import { COUPON_ALLOWED_GROUPS, hasAllowedGroup } from "../../ProtectedRoute";

const COLORS = {
  aquaBlue: "#11a8a4",
  grassGreen: "#7ed957",
  darkGrey: "#3c3b3f",
  vividPurple: "#9440dd",
  white: "#ffffff",
  lightGrey: "#f3f4f6",
  mediumGrey: "#e5e7eb",
  darkText: "#1f2937",
  mediumText: "#6b7280",
  lightText: "#9ca3af",
};


function formatDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function addressLine(a) {
  if (!a) return "—";
  return [a.address_line_1, a.address_line_2, a.village, a.gram_panchayat, a.block, a.district, a.state, a.pincode].filter(Boolean).join(", ") || "—";
}

export default function MfiFamiliesPage() {
  const { clientId: paramClientId, packageId: paramPackageId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Support both /coordinator/mfi/families/:clientId/:packageId and ?client_id=&package=
  const clientId = paramClientId || searchParams.get("client_id") || "";
  const packageId = paramPackageId || searchParams.get("package") || "";

  // Coupons entry buttons are a UI convenience only — backend 403 is the real
  // security. If groups are missing in localStorage, buttons stay visible.
  const canSeeCoupons = hasAllowedGroup(COUPON_ALLOWED_GROUPS);

  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedFamilyId, setExpandedFamilyId] = useState(null);
  const [viewMembersFamily, setViewMembersFamily] = useState(null);

  // ── Tabs: unassigned / assigned ──
  const [activeTab, setActiveTab] = useState("unassigned"); // "unassigned" | "assigned"

  // ── Assign Technician state ──
  const [selectedFamilyIds, setSelectedFamilyIds] = useState(new Set());
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [technicians, setTechnicians] = useState([]);
  const [techLoading, setTechLoading] = useState(false);
  const [techError, setTechError] = useState(null);
  const [techPickerId, setTechPickerId] = useState("");
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);
  const [servicesError, setServicesError] = useState(null);
  // Multi-technician assignments — one service set per technician:
  // [{ techId: "12", serviceIds: [3, 5] }]
  const [techAssignments, setTechAssignments] = useState([]);
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [assignError, setAssignError] = useState(null);
  const [toast, setToast] = useState(null); // { type: 'success'|'error', message }

  const showToast = useCallback((message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }, []);

  const loadFamilies = useCallback(async () => {
    if (!clientId || !packageId) {
      setError("Missing client or package ID");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await mfiApi.getActivatedFamilies(clientId, packageId);
      setFamilies(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load families");
      setFamilies([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, packageId]);

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  // Close modals on Esc
  useEffect(() => {
    if (!viewMembersFamily && !showAssignModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") {
        setViewMembersFamily(null);
        if (!assignSubmitting) setShowAssignModal(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewMembersFamily, showAssignModal, assignSubmitting]);

  // Derived: split by assigned_technician presence
  const unassignedFamilies = families.filter((f) => !f.assigned_technician);
  const assignedFamilies = families.filter((f) => !!f.assigned_technician);
  const displayedFamilies = activeTab === "unassigned" ? unassignedFamilies : assignedFamilies;

  // Clear selection when switching tabs or when families reload
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setSelectedFamilyIds(new Set());
    setAssignError(null);
  };

  // ── Selection helpers (only for displayed / unassigned tab) ──
  const toggleFamily = (familyId) => {
    setSelectedFamilyIds((prev) => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId);
      else next.add(familyId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedFamilyIds.size === displayedFamilies.length && displayedFamilies.length > 0) {
      setSelectedFamilyIds(new Set());
    } else {
      setSelectedFamilyIds(new Set(displayedFamilies.map((f) => f.family_id)));
    }
  };

  const clearSelection = () => setSelectedFamilyIds(new Set());

  // ── Multi-technician + per-technician services helpers ──
  const techNameOf = (techId) => {
    const t = technicians.find((x) => String(x.id) === String(techId));
    if (!t) return `ID ${techId}`;
    return t.name || t.user?.name || `Technician #${t.id}`;
  };

  const addTechnician = (techId) => {
    setAssignError(null);
    if (!techId) return;
    setTechAssignments((prev) =>
      prev.some((a) => a.techId === String(techId))
        ? prev
        : [...prev, { techId: String(techId), serviceIds: [] }]
    );
    setTechPickerId("");
  };

  const removeTechnician = (techId) => {
    setAssignError(null);
    setTechAssignments((prev) => prev.filter((a) => a.techId !== String(techId)));
  };

  const toggleService = (techId, serviceId) => {
    setAssignError(null);
    setTechAssignments((prev) =>
      prev.map((a) =>
        a.techId === String(techId)
          ? {
              ...a,
              serviceIds: a.serviceIds.includes(serviceId)
                ? a.serviceIds.filter((id) => id !== serviceId)
                : [...a.serviceIds, serviceId],
            }
          : a
      )
    );
  };

  const toggleAllServices = (techId) => {
    setAssignError(null);
    setTechAssignments((prev) =>
      prev.map((a) =>
        a.techId === String(techId)
          ? { ...a, serviceIds: a.serviceIds.length === services.length ? [] : services.map((s) => s.id) }
          : a
      )
    );
  };

  // ── Fetch technicians (filtered) ──
  const fetchTechnicians = useCallback(async () => {
    setTechLoading(true);
    setTechError(null);
    try {
      const data = await mfiApi.getTechnicians();
      // FILTER: only is_mfi === true, preferably status === "ACTIVE"
      const filtered = (Array.isArray(data) ? data : []).filter(
        (t) => t.is_mfi === true && t.status === "ACTIVE"
      );
      setTechnicians(filtered);
      if (filtered.length === 0) {
        setTechError("No eligible technicians found (is_mfi=true, status=ACTIVE).");
      }
    } catch (e) {
      setTechError(e?.response?.data?.detail || e?.response?.data?.message || e.message || "Failed to load technicians");
      setTechnicians([]);
    } finally {
      setTechLoading(false);
    }
  }, []);

  // ── Fetch services (only source: /api/services/) ──
  const fetchServices = useCallback(async () => {
    setServicesLoading(true);
    setServicesError(null);
    try {
      const data = await mfiApi.getServices();
      const list = Array.isArray(data) ? data : [];
      setServices(list);
      if (list.length === 0) setServicesError("No services found.");
    } catch (e) {
      setServicesError(e?.response?.data?.detail || e?.response?.data?.message || e.message || "Failed to load services");
      setServices([]);
    } finally {
      setServicesLoading(false);
    }
  }, []);

  const handleOpenAssignModal = () => {
    if (activeTab !== "unassigned") return;
    if (selectedFamilyIds.size === 0) {
      showToast("Please select at least one family.", "error");
      return;
    }
    setAssignError(null);
    setTechPickerId("");
    setTechAssignments([]);
    setShowAssignModal(true);
    // Fetch if not already loaded or if empty
    if (technicians.length === 0 && !techLoading) {
      fetchTechnicians();
    }
    if (services.length === 0 && !servicesLoading) {
      fetchServices();
    }
  };

  const handleAssignSubmit = async () => {
    if (selectedFamilyIds.size === 0) {
      setAssignError("Please select at least one family.");
      return;
    }
    if (techAssignments.length === 0) {
      setAssignError("Please add at least one technician.");
      return;
    }
    const missingServices = techAssignments.filter((a) => a.serviceIds.length === 0);
    if (missingServices.length > 0) {
      setAssignError(
        `Please select at least one service for: ${missingServices.map((a) => techNameOf(a.techId)).join(", ")}.`
      );
      return;
    }
    if (!packageId) {
      setAssignError("Missing package ID.");
      return;
    }
    setAssignSubmitting(true);
    setAssignError(null);
    // One POST per technician (existing endpoint shape) — same families, each
    // technician with their own service set. Built dynamically, nothing hard-coded.
    const numericPkg = Number(packageId);
    const payloadPackageId = Number.isNaN(numericPkg) ? packageId : numericPkg;
    const familyIds = Array.from(selectedFamilyIds);
    let done = 0;
    try {
      for (const a of techAssignments) {
        await mfiApi.assignTechnician({
          package_id: payloadPackageId,
          family_ids: familyIds,
          technician_id: String(a.techId),
          service_ids: a.serviceIds.map(Number),
        });
        done += 1;
      }
      showToast(
        `Successfully assigned ${familyIds.length} ${familyIds.length === 1 ? "family" : "families"} to ${done} ${done === 1 ? "technician" : "technicians"}.`,
        "success"
      );
      setShowAssignModal(false);
      setTechPickerId("");
      setTechAssignments([]);
      clearSelection();
      // Refresh family data so new assignments are visible (families move to Assigned tab)
      await loadFamilies();
    } catch (e) {
      const base =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.response?.data?.error ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        e.message ||
        "Failed to assign technician";
      const msg =
        done > 0
          ? `${done}/${techAssignments.length} technicians assigned. Failed on next: ${base}`
          : base;
      setAssignError(msg);
      showToast(msg, "error");
      // Refresh anyway — earlier technicians in the loop may already be assigned
      if (done > 0) {
        try {
          await loadFamilies();
        } catch {}
      }
    } finally {
      setAssignSubmitting(false);
    }
  };

  const isAllSelected = displayedFamilies.length > 0 && selectedFamilyIds.size === displayedFamilies.length;
  const isIndeterminate = selectedFamilyIds.size > 0 && selectedFamilyIds.size < displayedFamilies.length;

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh", backgroundColor: COLORS.lightGrey }}>
      {/* Header */}
      <div style={{ backgroundColor: COLORS.white, padding: "16px 24px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            <button
              onClick={() => navigate("/coordinator/mfi")}
              style={{ backgroundColor: COLORS.lightGrey, color: COLORS.darkText, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center", gap: 6 }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              MFI Clients
            </button>
            <h1 style={{ fontSize: 18, fontWeight: 700, color: COLORS.darkText, margin: 0 }}>Activated Families</h1>
            <span style={{ fontSize: 12, backgroundColor: `${COLORS.aquaBlue}15`, color: COLORS.aquaBlue, padding: "4px 8px", borderRadius: 12, fontWeight: 600 }}>
              {clientId} · Package {packageId}
            </span>
          </div>
          
        </div>
        <button onClick={loadFamilies} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 14px", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 6, color: COLORS.darkText }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
          Refresh
        </button>
      </div>

      <div style={{ flex: 1, padding: 24, maxWidth: 1200, width: "100%", margin: "0 auto", boxSizing: "border-box" }}>
        {/* Tabs */}
        {!loading && !error && families.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <button
              onClick={() => handleTabChange("unassigned")}
              style={{
                flex: 1,
                maxWidth: 220,
                padding: "10px 16px",
                borderRadius: 8,
                border: activeTab === "unassigned" ? `2px solid ${COLORS.aquaBlue}` : `1px solid ${COLORS.mediumGrey}`,
                backgroundColor: activeTab === "unassigned" ? `${COLORS.aquaBlue}12` : COLORS.white,
                color: activeTab === "unassigned" ? COLORS.aquaBlue : COLORS.mediumText,
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              Unassigned
              <span style={{ backgroundColor: activeTab === "unassigned" ? COLORS.aquaBlue : COLORS.mediumGrey, color: COLORS.white, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{unassignedFamilies.length}</span>
            </button>
            <button
              onClick={() => handleTabChange("assigned")}
              style={{
                flex: 1,
                maxWidth: 220,
                padding: "10px 16px",
                borderRadius: 8,
                border: activeTab === "assigned" ? `2px solid ${COLORS.grassGreen}` : `1px solid ${COLORS.mediumGrey}`,
                backgroundColor: activeTab === "assigned" ? `${COLORS.grassGreen}12` : COLORS.white,
                color: activeTab === "assigned" ? COLORS.grassGreen : COLORS.mediumText,
                fontWeight: 700,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
              }}
            >
              Assigned
              <span style={{ backgroundColor: activeTab === "assigned" ? COLORS.grassGreen : COLORS.mediumGrey, color: COLORS.white, borderRadius: 10, padding: "2px 8px", fontSize: 11, fontWeight: 700 }}>{assignedFamilies.length}</span>
            </button>
          </div>
        )}

        {loading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: 40, backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}` }}>
            <div style={{ width: 36, height: 36, border: `4px solid ${COLORS.mediumGrey}`, borderTop: `4px solid ${COLORS.aquaBlue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
            <p style={{ marginTop: 12, color: COLORS.mediumText, fontSize: 14 }}>Loading activated families...</p>
          </div>
        ) : error ? (
          <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: 20, textAlign: "center" }}>
            <p style={{ color: "#dc2626", fontSize: 14, margin: 0 }}>{error}</p>
            <button onClick={loadFamilies} style={{ marginTop: 12, backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13 }}>Retry</button>
          </div>
        ) : families.length === 0 ? (
          <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 40, textAlign: "center" }}>
            <p style={{ color: COLORS.mediumText, margin: 0, fontSize: 14 }}>No activated families found for {clientId} / Package {packageId}.</p>
            <p style={{ color: COLORS.lightText, margin: "6px 0 0 0", fontSize: 12 }}>This package may have no activations yet.</p>
          </div>
        ) : displayedFamilies.length === 0 ? (
          <div style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 12, padding: 40, textAlign: "center" }}>
            <p style={{ color: COLORS.mediumText, margin: 0, fontSize: 14 }}>
              {activeTab === "unassigned" ? "All families are assigned — no unassigned families." : "No assigned families yet."}
            </p>
            <p style={{ color: COLORS.lightText, margin: "6px 0 0 0", fontSize: 12 }}>
              {activeTab === "unassigned" ? "Switch to the Assigned tab to view assigned families." : "Assign a technician from the Unassigned tab."}
            </p>
          </div>
        ) : (
          <div style={{ backgroundColor: COLORS.white, borderRadius: 12, border: `1px solid ${COLORS.mediumGrey}`, overflow: "hidden" }}>
            <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: COLORS.lightGrey, flexWrap: "wrap", gap: 8 }}>
              <span style={{ fontSize: 13, color: COLORS.mediumText }}>
                {activeTab === "unassigned"
                  ? `${unassignedFamilies.length} unassigned ${unassignedFamilies.length === 1 ? "family" : "families"}`
                  : `${assignedFamilies.length} assigned ${assignedFamilies.length === 1 ? "family" : "families"}`}
                {activeTab === "unassigned" && selectedFamilyIds.size > 0 && <span style={{ marginLeft: 8, fontWeight: 600, color: COLORS.aquaBlue }}>· {selectedFamilyIds.size} selected</span>}
              </span>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {activeTab === "unassigned" && selectedFamilyIds.size > 0 && (
                  <button
                    onClick={clearSelection}
                    style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "7px 12px", cursor: "pointer", fontSize: 12, fontWeight: 500, color: COLORS.mediumText }}
                  >
                    Clear selection
                  </button>
                )}
                {activeTab === "unassigned" && (
                  <button
                    onClick={handleOpenAssignModal}
                    disabled={selectedFamilyIds.size === 0}
                    title={selectedFamilyIds.size === 0 ? "Select at least one family" : `Assign ${selectedFamilyIds.size} familie(s)`}
                    style={{
                      backgroundColor: selectedFamilyIds.size === 0 ? COLORS.mediumGrey : COLORS.aquaBlue,
                      color: COLORS.white,
                      border: "none",
                      borderRadius: 6,
                      padding: "8px 16px",
                      cursor: selectedFamilyIds.size === 0 ? "not-allowed" : "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      opacity: selectedFamilyIds.size === 0 ? 0.6 : 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>
                    Assign Technician
                  </button>
                )}
              </div>
            </div>
            {activeTab === "unassigned" && selectedFamilyIds.size === 0 && displayedFamilies.length > 0 && (
              <div style={{ padding: "8px 16px", backgroundColor: "#fffbeb", borderBottom: `1px solid #fde68a`, fontSize: 12, color: "#92400e", display: "flex", alignItems: "center", gap: 6 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                Select families using the checkboxes to enable “Assign Technician”.
              </div>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 980 }}>
                <thead>
                  <tr style={{ backgroundColor: COLORS.lightGrey, textAlign: "left", color: COLORS.mediumText }}>
                    {activeTab === "unassigned" && (
                      <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap", width: 40, textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          ref={(el) => { if (el) el.indeterminate = isIndeterminate; }}
                          onChange={toggleSelectAll}
                          style={{ width: 16, height: 16, cursor: "pointer", accentColor: COLORS.aquaBlue }}
                          aria-label="Select all families"
                        />
                      </th>
                    )}
                    <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>Family ID</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Family Head</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Mobile</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Loan No.</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600 }}>Package</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "right" }}>Price</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, minWidth: 200 }}>Location</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Status</th>
                    {activeTab === "assigned" && <th style={{ padding: "10px 12px", fontWeight: 600, minWidth: 180 }}>Assigned Technician</th>}
                    <th style={{ padding: "10px 12px", fontWeight: 600, whiteSpace: "nowrap" }}>Activated At</th>
                    <th style={{ padding: "10px 12px", fontWeight: 600, textAlign: "center" }}>Members</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedFamilies.map((fam) => {
                    const isExpanded = expandedFamilyId === fam.family_id;
                    const isSelected = selectedFamilyIds.has(fam.family_id);
                    return (
                      <React.Fragment key={fam.family_id}>
                        <tr style={{ borderTop: `1px solid ${COLORS.mediumGrey}`, backgroundColor: isSelected ? `${COLORS.aquaBlue}0f` : isExpanded ? `${COLORS.aquaBlue}06` : COLORS.white }}>
                          {activeTab === "unassigned" && (
                            <td style={{ padding: "12px", textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleFamily(fam.family_id)}
                                style={{ width: 16, height: 16, cursor: "pointer", accentColor: COLORS.aquaBlue }}
                                aria-label={`Select ${fam.family_id}`}
                              />
                            </td>
                          )}
                          <td style={{ padding: "12px", fontWeight: 600, color: COLORS.aquaBlue, whiteSpace: "nowrap" }}>{fam.family_id}</td>
                          <td style={{ padding: 12, color: COLORS.darkText }}>
                            <div style={{ fontWeight: 600 }}>{fam.family_head?.name || "—"}</div>
                            <div style={{ fontSize: 11, color: COLORS.lightText }}>{fam.family_head?.member_id || ""}</div>
                          </td>
                          <td style={{ padding: 12, color: COLORS.darkText, whiteSpace: "nowrap" }}>
                            <div>{fam.family_head?.mobile || fam.primary_mobile || "—"}</div>
                            <div style={{ fontSize: 11, color: COLORS.lightText }}>Primary: {fam.primary_mobile || "—"}</div>
                          </td>
                          <td style={{ padding: 12, color: COLORS.darkText }}>{fam.family_head?.loan_number || "—"}</td>
                          <td style={{ padding: 12, color: COLORS.darkText }}>{fam.package?.name || "—"}</td>
                          <td style={{ padding: 12, textAlign: "right", fontWeight: 600, color: COLORS.darkText }}>₹{Number(fam.package?.price || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: 12, color: COLORS.mediumText, fontSize: 12, maxWidth: 260 }}>{addressLine(fam.address)}</td>
                          <td style={{ padding: 12, textAlign: "center" }}>
                            {fam.finance_activation?.is_activated ? (
                              <span style={{ fontSize: 11, fontWeight: 700, backgroundColor: `${COLORS.grassGreen}20`, color: COLORS.grassGreen, padding: "3px 8px", borderRadius: 10 }}>Activated</span>
                            ) : (
                              <span style={{ fontSize: 11, fontWeight: 600, backgroundColor: COLORS.lightGrey, color: COLORS.mediumText, padding: "3px 8px", borderRadius: 10 }}>Pending</span>
                            )}
                            {fam.finance_activation?.activated_by && <div style={{ fontSize: 10, color: COLORS.lightText, marginTop: 2 }}>by {fam.finance_activation.activated_by}</div>}
                          </td>
                          {activeTab === "assigned" && (
                            <td style={{ padding: 12, fontSize: 12 }}>
                              {fam.assigned_technician ? (
                                <div>
                                  <div style={{ fontWeight: 600, color: COLORS.darkText }}>{fam.assigned_technician.technician_name || `ID ${fam.assigned_technician.technician_id}`}</div>
                                  <div style={{ fontSize: 11, color: COLORS.mediumText }}>ID: {fam.assigned_technician.technician_id}</div>
                                  {fam.assigned_technician.assigned_at && <div style={{ fontSize: 10, color: COLORS.lightText, marginTop: 2 }}>{formatDate(fam.assigned_technician.assigned_at)}</div>}
                                  {fam.assigned_technician.assigned_by && <div style={{ fontSize: 10, color: COLORS.lightText }}>by {fam.assigned_technician.assigned_by}</div>}
                                </div>
                              ) : (
                                <span style={{ fontSize: 11, color: COLORS.lightText }}>—</span>
                              )}
                            </td>
                          )}
                          <td style={{ padding: 12, whiteSpace: "nowrap", fontSize: 11, color: COLORS.mediumText }}>{formatDate(fam.finance_activation?.activated_at)}</td>
                          <td style={{ padding: 12, textAlign: "center" }}>
                            <div style={{ display: "flex", gap: 6, justifyContent: "center", flexWrap: "wrap" }}>
                              <button
                                onClick={() => setViewMembersFamily(fam)}
                                style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: COLORS.aquaBlue, whiteSpace: "nowrap" }}
                              >
                                View ({fam.members?.length || 0})
                              </button>
                              {canSeeCoupons && (
                                <button
                                  onClick={() => navigate(`/coordinator/mfi/families/${encodeURIComponent(clientId)}/${encodeURIComponent(packageId)}/coupons/${encodeURIComponent(fam.family_id)}`)}
                                  style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 10px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: COLORS.vividPurple, whiteSpace: "nowrap" }}
                                >
                                  Coupons
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Assign Technician Modal — only for unassigned tab */}
      {showAssignModal && activeTab === "unassigned" && (
        <div
          onClick={() => !assignSubmitting && setShowAssignModal(false)}
          style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 20 }}
          role="dialog"
          aria-modal="true"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: COLORS.white, borderRadius: 12, width: "100%", maxWidth: 480, display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
          >
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: COLORS.lightGrey }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.darkText }}>Assign Technician</h3>
                <p style={{ margin: "4px 0 0 0", fontSize: 12, color: COLORS.mediumText }}>
                  Selected Families: <strong style={{ color: COLORS.aquaBlue }}>{selectedFamilyIds.size}</strong> · Package {packageId}
                </p>
              </div>
              <button
                onClick={() => !assignSubmitting && setShowAssignModal(false)}
                disabled={assignSubmitting}
                style={{ background: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, width: 32, height: 32, cursor: assignSubmitting ? "not-allowed" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.mediumText }}
              >
                ✕
              </button>
            </div>

            <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 12 }}>
              <div style={{ backgroundColor: `${COLORS.aquaBlue}08`, border: `1px solid ${COLORS.aquaBlue}30`, borderRadius: 8, padding: "10px 12px", fontSize: 12, color: COLORS.darkText }}>
                <div style={{ fontWeight: 600, marginBottom: 4, color: COLORS.aquaBlue }}>Family IDs:</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, maxHeight: 60, overflowY: "auto" }}>
                  {Array.from(selectedFamilyIds).map((fid) => (
                    <span key={fid} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 500 }}>{fid}</span>
                  ))}
                </div>
              </div>

              <label style={{ fontSize: 13, fontWeight: 600, color: COLORS.darkText }}>
                Technicians <span style={{ color: "#dc2626" }}>*</span>
                {techAssignments.length > 0 && (
                  <span style={{ fontWeight: 600, color: COLORS.aquaBlue, marginLeft: 6, fontSize: 11 }}>
                    {techAssignments.length} selected
                  </span>
                )}
              </label>

              {techLoading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, backgroundColor: COLORS.lightGrey, borderRadius: 8, border: `1px solid ${COLORS.mediumGrey}` }}>
                  <div style={{ width: 18, height: 18, border: `3px solid ${COLORS.mediumGrey}`, borderTop: `3px solid ${COLORS.aquaBlue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  <span style={{ fontSize: 13, color: COLORS.mediumText }}>Loading technicians...</span>
                </div>
              ) : techError && technicians.length === 0 ? (
                <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#dc2626", fontSize: 12 }}>{techError}</span>
                  <button onClick={fetchTechnicians} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>Retry</button>
                </div>
              ) : (
                <div style={{ display: "flex", gap: 8 }}>
                  <select
                    value={techPickerId}
                    onChange={(e) => { setTechPickerId(e.target.value); setAssignError(null); }}
                    disabled={assignSubmitting}
                    style={{
                      flex: 1,
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `1px solid ${COLORS.mediumGrey}`,
                      fontSize: 13,
                      color: COLORS.darkText,
                      backgroundColor: COLORS.white,
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <option value="">— Add a technician —</option>
                    {technicians
                      .filter((t) => !techAssignments.some((a) => a.techId === String(t.id)))
                      .map((t) => (
                        <option key={t.id} value={String(t.id)}>
                          {t.name || t.user?.name || `Technician #${t.id}`}
                          {t.qualification ? ` — ${t.qualification}` : ""}
                          {t.services?.length ? ` (${t.services.map((s) => s.name).join(", ")})` : ""}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => addTechnician(techPickerId)}
                    disabled={assignSubmitting || !techPickerId}
                    style={{
                      backgroundColor: !techPickerId ? COLORS.mediumGrey : COLORS.aquaBlue,
                      color: COLORS.white,
                      border: "none",
                      borderRadius: 8,
                      padding: "10px 16px",
                      cursor: !techPickerId ? "not-allowed" : "pointer",
                      fontSize: 13,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Add
                  </button>
                </div>
              )}

              {technicians.length > 0 && !techLoading && (
                <div style={{ fontSize: 11, color: COLORS.lightText }}>{technicians.length} eligible technician{technicians.length !== 1 ? "s" : ""} available</div>
              )}

              {/* Per-technician service cards */}
              {servicesLoading ? (
                <div style={{ display: "flex", alignItems: "center", gap: 10, padding: 14, backgroundColor: COLORS.lightGrey, borderRadius: 8, border: `1px solid ${COLORS.mediumGrey}` }}>
                  <div style={{ width: 18, height: 18, border: `3px solid ${COLORS.mediumGrey}`, borderTop: `3px solid ${COLORS.aquaBlue}`, borderRadius: "50%", animation: "spin 1s linear infinite" }} />
                  <span style={{ fontSize: 13, color: COLORS.mediumText }}>Loading services...</span>
                </div>
              ) : servicesError && services.length === 0 ? (
                <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                  <span style={{ color: "#dc2626", fontSize: 12 }}>{servicesError}</span>
                  <button onClick={fetchServices} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "6px 12px", cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>Retry</button>
                </div>
              ) : techAssignments.length === 0 ? (
                <div style={{ backgroundColor: COLORS.lightGrey, border: `1px dashed ${COLORS.mediumGrey}`, borderRadius: 8, padding: 14, fontSize: 12, color: COLORS.mediumText, textAlign: "center" }}>
                  No technicians added yet — pick one above and press Add, then tick its services.
                </div>
              ) : (
                techAssignments.map((a) => (
                  <div key={a.techId} style={{ border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 8, overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, padding: "9px 12px", backgroundColor: `${COLORS.aquaBlue}10`, borderBottom: `1px solid ${COLORS.mediumGrey}`, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.darkText }}>
                        {techNameOf(a.techId)}
                        <span style={{ fontWeight: 500, color: a.serviceIds.length > 0 ? COLORS.aquaBlue : COLORS.lightText, marginLeft: 6, fontSize: 11 }}>
                          {a.serviceIds.length} of {services.length} service{services.length !== 1 ? "s" : ""}
                        </span>
                      </span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {services.length > 0 && (
                          <button
                            type="button"
                            onClick={() => toggleAllServices(a.techId)}
                            disabled={assignSubmitting}
                            style={{ background: "none", border: "none", padding: 0, cursor: "pointer", fontSize: 11, fontWeight: 600, color: COLORS.aquaBlue, textDecoration: "underline" }}
                          >
                            {a.serviceIds.length === services.length ? "Clear all" : "Select all"}
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => removeTechnician(a.techId)}
                          disabled={assignSubmitting}
                          title={`Remove ${techNameOf(a.techId)}`}
                          style={{ background: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, width: 26, height: 26, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.mediumText, fontSize: 12 }}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <div style={{ maxHeight: 170, overflowY: "auto", padding: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                      {services.length === 0 && (
                        <div style={{ padding: 12, fontSize: 12, color: COLORS.lightText, textAlign: "center" }}>No services available.</div>
                      )}
                      {services.map((s) => {
                        const checked = a.serviceIds.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 9,
                              padding: "7px 9px",
                              borderRadius: 6,
                              cursor: assignSubmitting ? "not-allowed" : "pointer",
                              fontSize: 13,
                              color: COLORS.darkText,
                              backgroundColor: checked ? `${COLORS.aquaBlue}14` : COLORS.white,
                              border: `1px solid ${checked ? `${COLORS.aquaBlue}55` : "transparent"}`,
                              opacity: assignSubmitting ? 0.6 : 1,
                              userSelect: "none",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleService(a.techId, s.id)}
                              disabled={assignSubmitting}
                              style={{ width: 15, height: 15, accentColor: COLORS.aquaBlue, cursor: "pointer", margin: 0, flexShrink: 0 }}
                            />
                            <span style={{ fontWeight: checked ? 600 : 500, flex: 1 }}>{s.name}</span>
                            {s.id != null && <span style={{ fontSize: 11, color: COLORS.lightText }}>#{s.id}</span>}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}

              {assignError && (
                <div style={{ backgroundColor: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 12px", fontSize: 12, color: "#dc2626" }}>{assignError}</div>
              )}
            </div>

            <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "flex-end", gap: 10, backgroundColor: COLORS.white }}>
              <button
                onClick={() => setShowAssignModal(false)}
                disabled={assignSubmitting}
                style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "9px 16px", cursor: assignSubmitting ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 500, color: COLORS.darkText, opacity: assignSubmitting ? 0.6 : 1 }}
              >
                Cancel
              </button>
              <button
                onClick={handleAssignSubmit}
                disabled={assignSubmitting || techAssignments.length === 0 || techAssignments.some((a) => a.serviceIds.length === 0) || selectedFamilyIds.size === 0}
                style={{
                  backgroundColor: assignSubmitting || techAssignments.length === 0 || techAssignments.some((a) => a.serviceIds.length === 0) ? COLORS.mediumGrey : COLORS.aquaBlue,
                  color: COLORS.white,
                  border: "none",
                  borderRadius: 6,
                  padding: "9px 18px",
                  cursor: assignSubmitting || techAssignments.length === 0 || techAssignments.some((a) => a.serviceIds.length === 0) ? "not-allowed" : "pointer",
                  fontSize: 13,
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  opacity: assignSubmitting || techAssignments.length === 0 || techAssignments.some((a) => a.serviceIds.length === 0) ? 0.7 : 1,
                }}
              >
                {assignSubmitting && <span style={{ width: 14, height: 14, border: `2px solid ${COLORS.white}`, borderTop: "2px solid transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />}
                {assignSubmitting ? "Assigning..." : "Assign Technician"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Members Modal */}
      {viewMembersFamily && (
        <div onClick={() => setViewMembersFamily(null)} style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.45)", display: "flex", justifyContent: "center", alignItems: "center", zIndex: 1000, padding: 20 }} role="dialog" aria-modal="true">
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ backgroundColor: COLORS.white, borderRadius: 12, width: "100%", maxWidth: 720, maxHeight: "85vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 40px rgba(0,0,0,0.15)" }}
          >
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: COLORS.lightGrey }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.darkText }}>{viewMembersFamily.family_id} — {viewMembersFamily.family_head?.name}</h3>
                <p style={{ margin: "4px 0 0 0", fontSize: 12, color: COLORS.mediumText }}>{addressLine(viewMembersFamily.address)} · {viewMembersFamily.members?.length || 0} members</p>
              </div>
              <button onClick={() => setViewMembersFamily(null)} style={{ background: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, width: 32, height: 32, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.mediumText }}>✕</button>
            </div>
            <div style={{ overflowY: "auto", padding: 16 }}>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ backgroundColor: COLORS.lightGrey, textAlign: "left", color: COLORS.mediumText }}>
                      <th style={{ padding: "8px 10px", fontWeight: 600 }}>Member</th>
                      <th style={{ padding: "8px 10px", fontWeight: 600 }}>Relation</th>
                      <th style={{ padding: "8px 10px", fontWeight: 600 }}>Age/Gender</th>
                      <th style={{ padding: "8px 10px", fontWeight: 600 }}>Loan</th>
                      <th style={{ padding: "8px 10px", fontWeight: 600 }}>Contact</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewMembersFamily.members || []).map((m) => (
                      <tr key={m.member_id} style={{ borderTop: `1px solid ${COLORS.mediumGrey}` }}>
                        <td style={{ padding: "10px", color: COLORS.darkText }}>
                          <div style={{ fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                            {m.name}
                            {m.is_primary_contact && <span style={{ fontSize: 10, backgroundColor: `${COLORS.aquaBlue}15`, color: COLORS.aquaBlue, padding: "2px 6px", borderRadius: 10, fontWeight: 700 }}>Head</span>}
                          </div>
                          <div style={{ fontSize: 11, color: COLORS.lightText }}>{m.member_id}</div>
                        </td>
                        <td style={{ padding: "10px", color: COLORS.mediumText, textTransform: "capitalize" }}>{m.relationship || "—"}</td>
                        <td style={{ padding: "10px", color: COLORS.mediumText }}>
                          {[m.age ? `${m.age} yrs` : "", m.gender].filter(Boolean).join(" · ") || "—"}
                          {m.date_of_birth && <div style={{ fontSize: 11, color: COLORS.lightText }}>DOB {m.date_of_birth}</div>}
                        </td>
                        <td style={{ padding: "10px", color: COLORS.mediumText }}>{m.loan_number || "—"}</td>
                        <td style={{ padding: "10px", color: COLORS.mediumText, fontSize: 12 }}>
                          <div>{m.mobile || "—"}</div>
                          {m.alternate_mobile && <div style={{ color: COLORS.lightText }}>Alt: {m.alternate_mobile}</div>}
                          {m.email && m.email !== "False" && <div style={{ color: COLORS.lightText }}>{m.email}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {viewMembersFamily.assigned_technician && (
                <div style={{ marginTop: 16, backgroundColor: `${COLORS.grassGreen}10`, border: `1px solid ${COLORS.grassGreen}30`, borderRadius: 8, padding: "10px 12px", fontSize: 12 }}>
                  <div style={{ fontWeight: 700, color: COLORS.grassGreen, marginBottom: 4 }}>Assigned Technician</div>
                  <div style={{ color: COLORS.darkText, fontWeight: 600 }}>{viewMembersFamily.assigned_technician.technician_name} (ID {viewMembersFamily.assigned_technician.technician_id})</div>
                  <div style={{ color: COLORS.mediumText, fontSize: 11, marginTop: 2 }}>Assigned at {formatDate(viewMembersFamily.assigned_technician.assigned_at)} · by {viewMembersFamily.assigned_technician.assigned_by || "—"}</div>
                </div>
              )}
            </div>
            <div style={{ padding: "12px 16px", borderTop: `1px solid ${COLORS.mediumGrey}`, display: "flex", justifyContent: "flex-end", gap: 8, backgroundColor: COLORS.white }}>
              {canSeeCoupons && (
                <button
                  onClick={() => {
                    const fam = viewMembersFamily;
                    setViewMembersFamily(null);
                    navigate(`/coordinator/mfi/families/${encodeURIComponent(clientId)}/${encodeURIComponent(packageId)}/coupons/${encodeURIComponent(fam.family_id)}`);
                  }}
                  style={{ backgroundColor: COLORS.aquaBlue, color: COLORS.white, border: "none", borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                >
                  View Family Coupons
                </button>
              )}
              <button onClick={() => setViewMembersFamily(null)} style={{ backgroundColor: COLORS.white, border: `1px solid ${COLORS.mediumGrey}`, borderRadius: 6, padding: "8px 16px", cursor: "pointer", fontSize: 13, fontWeight: 500 }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 2000, backgroundColor: toast.type === "success" ? COLORS.grassGreen : "#ef4444", color: COLORS.white, padding: "12px 18px", borderRadius: 8, fontSize: 13, fontWeight: 500, boxShadow: "0 8px 24px rgba(0,0,0,0.15)", display: "flex", alignItems: "center", gap: 8, maxWidth: 420 }}>
          <span style={{ fontSize: 16 }}>{toast.type === "success" ? "✓" : "✕"}</span>
          {toast.message}
        </div>
      )}

      <style>{`@keyframes spin{0%{transform:rotate(0deg)}100%{transform:rotate(360deg)}}`}</style>
    </div>
  );
}
